import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHeader } from '@/contexts/HeaderContext';

const Section = ({ icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #e0ddd6' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '18px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Icon icon={icon} style={{ width: 20, height: 20, color: '#0a0a0a', flexShrink: 0 }} />
        <span style={{ flex: 1, fontFamily: 'Manrope-Variable, serif', fontSize: 15, fontWeight: 600, color: '#0a0a0a', letterSpacing: '-0.01em' }}>
          {title}
        </span>
        <Icon
          icon="solar:alt-arrow-down-linear"
          style={{ width: 14, height: 14, color: '#9a9a9a', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '0 20px 20px', color: '#3a3a3a', fontFamily: 'Manrope-Variable, sans-serif', fontSize: 14, lineHeight: 1.7, letterSpacing: '-0.005em' }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Tip = ({ children }) => (
  <div style={{ background: '#f0ede6', borderRadius: 10, padding: '12px 16px', marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
    <Icon icon="solar:lightbulb-linear" style={{ width: 16, height: 16, color: '#D97757', flexShrink: 0, marginTop: 2 }} />
    <span style={{ fontSize: 13, color: '#5a5a5a', lineHeight: 1.6 }}>{children}</span>
  </div>
);

const Badge = ({ label, color = '#84A98C' }) => (
  <span style={{ display: 'inline-block', background: color, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, marginLeft: 6, verticalAlign: 'middle' }}>
    {label}
  </span>
);

const Guide = () => {
  const { t, i18n } = useTranslation();
  const { setHeaderProps } = useHeader();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  useEffect(() => {
    setHeaderProps({ title: t('guide.title') });
  }, [t, setHeaderProps]);

  return (
    <div style={{ background: 'hsl(var(--background))', paddingBottom: 100 }}>
      {/* Hero */}
      <div style={{ padding: '32px 20px 24px', borderBottom: '1px solid #e0ddd6' }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: 8 }}>
          {t('guide.subtitle')}
        </div>
        <h1 style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 24, fontWeight: 700, color: '#0a0a0a', letterSpacing: '-0.02em', lineHeight: 1.2, margin: 0 }}>
          {t('guide.heroTitle')}
        </h1>
        <p style={{ fontFamily: 'Manrope-Variable, sans-serif', fontSize: 14, color: '#6a6a6a', marginTop: 10, lineHeight: 1.6 }}>
          {t('guide.heroDescription')}
        </p>
      </div>

      {/* Sections */}
      <Section icon="solar:heart-linear" title={t('guide.whatIs.title')} defaultOpen={true}>
        <p>{t('guide.whatIs.p1')}</p>
        <p style={{ marginTop: 10 }}>{t('guide.whatIs.p2')}</p>
        <Tip>{t('guide.whatIs.tip')}</Tip>
      </Section>

      <Section icon="solar:login-3-linear" title={t('guide.gettingStarted.title')}>
        <p>{t('guide.gettingStarted.p1')}</p>
        <ol style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 8 }}><strong>{t('guide.gettingStarted.step1Title')}</strong> — {t('guide.gettingStarted.step1Desc')}</li>
          <li style={{ marginBottom: 8 }}><strong>{t('guide.gettingStarted.step2Title')}</strong> — {t('guide.gettingStarted.step2Desc')}</li>
          <li style={{ marginBottom: 8 }}><strong>{t('guide.gettingStarted.step3Title')}</strong> — {t('guide.gettingStarted.step3Desc')}</li>
        </ol>
        <Tip>{t('guide.gettingStarted.tip')}</Tip>
      </Section>

      <Section icon="solar:home-2-linear" title={t('guide.dashboard.title')}>
        <p>{t('guide.dashboard.p1')}</p>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon icon="solar:check-circle-linear" style={{ width: 16, height: 16, color: '#84A98C' }} />
            <span><strong>{t('guide.dashboard.doneLabel')}</strong> — {t('guide.dashboard.doneDesc')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon icon="solar:skip-next-linear" style={{ width: 16, height: 16, color: '#9a9a9a' }} />
            <span><strong>{t('guide.dashboard.skipLabel')}</strong> — {t('guide.dashboard.skipDesc')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon icon="solar:refresh-linear" style={{ width: 16, height: 16, color: '#D97757' }} />
            <span><strong>{t('guide.dashboard.altLabel')}</strong> — {t('guide.dashboard.altDesc')}</span>
          </div>
        </div>
        <p style={{ marginTop: 10 }}>{t('guide.dashboard.p2')}</p>
        <Tip>{t('guide.dashboard.tip')}</Tip>
      </Section>

      <Section icon="solar:tree-linear" title={t('guide.forest.title')}>
        <p>{t('guide.forest.p1')}</p>
        <p style={{ marginTop: 10 }}>{t('guide.forest.p2')}</p>
        <div style={{ background: '#f0ede6', borderRadius: 10, padding: '14px 16px', marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#5a5a5a', lineHeight: 1.7 }}>
            🌱 {t('guide.forest.level1')}<br />
            🌿 {t('guide.forest.level2')}<br />
            🌳 {t('guide.forest.level3')}<br />
            🌲 {t('guide.forest.level4')}
          </div>
        </div>
      </Section>

      <Section icon="solar:moon-linear" title={t('guide.checkin.title')}>
        <Badge label="Premium" color="#D97757" />
        <p style={{ marginTop: 8 }}>{t('guide.checkin.p1')}</p>
        <ol style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 6 }}>{t('guide.checkin.step1')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.checkin.step2')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.checkin.step3')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.checkin.step4')}</li>
        </ol>
        <Tip>{t('guide.checkin.tip')}</Tip>
      </Section>

      <Section icon="solar:chart-2-linear" title={t('guide.stats.title')}>
        <p>{t('guide.stats.p1')}</p>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#0a0a0a' }} />
            <span>{t('guide.stats.dotDone')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#e0ddd6' }} />
            <span>{t('guide.stats.dotSkipped')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', border: '1px solid #ccc', background: 'transparent' }} />
            <span>{t('guide.stats.dotEmpty')}</span>
          </div>
        </div>
        <p style={{ marginTop: 10 }}>{t('guide.stats.p2')}</p>
        <div style={{ background: '#f0ede6', borderRadius: 10, padding: '14px 16px', marginTop: 12, fontSize: 13, color: '#5a5a5a', lineHeight: 1.7 }}>
          {t('guide.stats.levels')}
        </div>
      </Section>

      <Section icon="solar:notebook-linear" title={t('guide.notes.title')}>
        <p>{t('guide.notes.p1')}</p>
        <p style={{ marginTop: 10 }}>{t('guide.notes.p2')}</p>
        <Tip>{t('guide.notes.tip')}</Tip>
      </Section>

      <Section icon="solar:calendar-linear" title={t('guide.events.title')}>
        <p>{t('guide.events.p1')}</p>
        <ul style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 6 }}>💍 {t('guide.events.type1')}</li>
          <li style={{ marginBottom: 6 }}>🎂 {t('guide.events.type2')}</li>
          <li style={{ marginBottom: 6 }}>☀️ {t('guide.events.type3')}</li>
          <li style={{ marginBottom: 6 }}>🎁 {t('guide.events.type4')}</li>
        </ul>
        <p style={{ marginTop: 10 }}>{t('guide.events.p2')}</p>
        <Tip>{t('guide.events.tip')}</Tip>
      </Section>

      <Section icon="solar:user-linear" title={t('guide.profile.title')}>
        <p>{t('guide.profile.p1')}</p>
        <ul style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 6 }}>{t('guide.profile.item1')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.profile.item2')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.profile.item3')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.profile.item4')}</li>
        </ul>
      </Section>

      <Section icon="solar:settings-linear" title={t('guide.settings.title')}>
        <p>{t('guide.settings.p1')}</p>
        <ul style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 6 }}>{t('guide.settings.item1')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.settings.item2')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.settings.item3')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.settings.item4')}</li>
        </ul>
      </Section>

      <Section icon="solar:card-linear" title={t('guide.subscription.title')}>
        <p>{t('guide.subscription.p1')}</p>
        <div style={{ marginTop: 14, display: 'flex', gap: 12 }}>
          {/* Free tier */}
          <div style={{ flex: 1, background: '#f0ede6', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Free</div>
            <ul style={{ paddingLeft: 16, fontSize: 12, color: '#5a5a5a', lineHeight: 1.8 }}>
              <li>{t('guide.subscription.free1')}</li>
              <li>{t('guide.subscription.free2')}</li>
              <li>{t('guide.subscription.free3')}</li>
            </ul>
          </div>
          {/* Premium tier */}
          <div style={{ flex: 1, background: '#0a0a0a', borderRadius: 10, padding: '14px 16px', color: '#f8f6f1' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Premium</div>
            <ul style={{ paddingLeft: 16, fontSize: 12, color: '#ccc', lineHeight: 1.8 }}>
              <li>{t('guide.subscription.prem1')}</li>
              <li>{t('guide.subscription.prem2')}</li>
              <li>{t('guide.subscription.prem3')}</li>
              <li>{t('guide.subscription.prem4')}</li>
            </ul>
          </div>
        </div>
        <p style={{ marginTop: 12, fontSize: 13, color: '#9a9a9a' }}>{t('guide.subscription.p2')}</p>
      </Section>

      <Section icon="solar:shield-linear" title={t('guide.support.title')}>
        <p>{t('guide.support.p1')}</p>
        <ul style={{ paddingLeft: 20, marginTop: 10 }}>
          <li style={{ marginBottom: 6 }}>{t('guide.support.item1')}</li>
          <li style={{ marginBottom: 6 }}>{t('guide.support.item2')}</li>
        </ul>
        <p style={{ marginTop: 10 }}>{t('guide.support.p2')}</p>
      </Section>

      <Section icon="solar:question-circle-linear" title={t('guide.faq.title')}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('guide.faq.q1')}</p>
          <p style={{ color: '#5a5a5a' }}>{t('guide.faq.a1')}</p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('guide.faq.q2')}</p>
          <p style={{ color: '#5a5a5a' }}>{t('guide.faq.a2')}</p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('guide.faq.q3')}</p>
          <p style={{ color: '#5a5a5a' }}>{t('guide.faq.a3')}</p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('guide.faq.q4')}</p>
          <p style={{ color: '#5a5a5a' }}>{t('guide.faq.a4')}</p>
        </div>
        <div>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('guide.faq.q5')}</p>
          <p style={{ color: '#5a5a5a' }}>{t('guide.faq.a5')}</p>
        </div>
      </Section>
    </div>
  );
};

export default Guide;
